import {
  SanityProductFragment,
  SanityProductOption,
  SanityProductOptionName,
  SanityProductOptionSetting,
  SanityProductVariantFragment,
  SanityProductVariantOption,
} from '@data/sanity/queries/types/product'
import { hasObject } from './helpers'

export interface Filter {
  name: string
  values: string[]
  productIds?: number[]
}

export interface FilterValue {
  name: string
  value: string
}

interface ProductInventoryVariant {
  id: number
  inStock: boolean
  lowStock: boolean
}

export interface ProductInventory {
  inStock: boolean
  lowStock: boolean
  variants: ProductInventoryVariant[]
}

export const productLowStockAmount = 10

export const productVariantLowStockAmount = 5

/**
 * Gets formatted product title with removed pipe characters and in uppercase.
 */
export const getFormattedProductTitle = (
  locale: Intl.LocalesArgument,
  title: string,
) => {
  return title
    .toLocaleUpperCase(locale)
    .replace(/\|/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/**
 * Converts Sanity product or variant ID string into Shopify ID number.
 */
export const sanityProductIdToShopifyId = (sanityProductId: string) => {
  const shopifyProductId = sanityProductId.split('-')?.[1]

  if (!shopifyProductId) {
    return null
  }

  return Number(shopifyProductId)
}

/**
 * Gets product option label.
 */
export const getOptionListLabel = (
  optionNames: SanityProductOptionName[],
  option: SanityProductOption,
) =>
  optionNames?.find(({ forOption }) => forOption === option.name)?.name ||
  option.name

/**
 * Gets default product option.
 */
export const getDefaultOption = (
  options: SanityProductOption[],
  optionSettings: SanityProductOptionSetting[],
): SanityProductVariantOption | undefined => {
  if (options.length === 0) {
    return
  }

  const firstOption = options?.[0]
  const defaultOption = {
    name: firstOption?.name,
    value: firstOption?.values?.[0],
    position: firstOption?.position,
  }

  if (optionSettings.length === 0) {
    return defaultOption
  }

  // Use first option setting to find default option
  const settingParts = optionSettings[0].forOption?.split(':')
  const name = settingParts?.[0]
  const value = settingParts?.[1]
  const position = options.find(
    (option) => option.name.toLowerCase() === name?.toLowerCase(),
  )?.position

  if (
    typeof name === 'undefined' ||
    typeof value === 'undefined' ||
    typeof position === 'undefined'
  ) {
    return defaultOption
  }

  return { name, value, position }
}

/**
 * Gets a product variant by the default option.
 */
export const getVariantByDefaultOption = (
  variants: SanityProductVariantFragment[],
  defaultOption?: SanityProductVariantOption,
) => {
  if (!defaultOption) {
    return null
  }

  const variant = variants.find(({ options }) =>
    options.some(
      (option) =>
        option.name.toLowerCase() === defaultOption.name.toLowerCase() &&
        option.value.toLowerCase() === defaultOption.value.toLowerCase(),
    ),
  )

  return variant ?? null
}

/**
 * Gets variant using active options, option name and value.
 */
export const getVariantFromOptions = (
  variants: SanityProductVariantFragment[],
  variantOptions: SanityProductVariantOption[],
  optionName: string,
  optionValue: string,
) => {
  const newVariantOptions = variantOptions.map((variantOption) => {
    const newVariantOption: SanityProductVariantOption = {
      position: variantOption.position,
      name: variantOption.name,
      value: variantOption.value,
    }

    if (variantOption.name === optionName) {
      newVariantOption.value = optionValue
    }

    return newVariantOption
  })

  // Find variant that matches all new options
  return variants.find((variant) =>
    variant.options.every((variantOption) => {
      const currentVariantOption: SanityProductVariantOption = {
        position: variantOption.position,
        name: variantOption.name,
        value: variantOption.value,
      }

      return hasObject(newVariantOptions, currentVariantOption)
    }),
  )
}

/**
 * Gets fallback variant using option name and value.
 */
export const getFallbackVariantFromOptions = (
  variants: SanityProductVariantFragment[],
  optionName: string,
  optionValue: string,
) =>
  variants.find((variant) =>
    variant.options.some(
      (variantOption) =>
        variantOption.name === optionName &&
        variantOption.value === optionValue,
    ),
  )

/**
 * Gets product key for lists of products in React.
 */
export const getProductKey = (product: SanityProductFragment) => {
  const variantKey =
    product.variants
      ?.map((variant) => variant.variantID)
      ?.filter(Boolean)
      ?.join('-') ?? 'defaultVariant'

  return `${product.productID}-${variantKey}`
}
